import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowsClockwise, Warning } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD_LABEL = { owner_name: 'Ιδιοκτήτης', apartment_number: 'Αρ. Διαμερίσματος' };

const SyncRow = ({ d, checked, onToggle }) => (
  <label className={`flex gap-3 p-3 border ${d.status === 'ownership_changed' ? 'border-amber-300 bg-amber-50' : 'border-zinc-200'} cursor-pointer`} data-testid={`owner-sync-row-${d.apartment_id}`}>
    <input type="checkbox" checked={checked} onChange={onToggle} className="mt-1 shrink-0 h-4 w-4 self-start" data-testid={`owner-sync-check-${d.apartment_id}`} />
    <div className="text-sm flex-1">
      <p className="font-semibold">Διαμέρισμα {d.apartment_number}</p>
      {Object.entries(d.changes).map(([f, ch]) => (
        <p key={f} className="text-xs mt-0.5">
          <span className="text-zinc-500">{FIELD_LABEL[f]}:</span>{' '}
          <span className="line-through text-zinc-400">{ch.old || '(κενό)'}</span> → <span className="font-medium text-emerald-700">{ch.new}</span>
        </p>
      ))}
      {d.warnings.map((w) => <p key={w} className="text-xs text-amber-700 mt-1 flex items-center gap-1"><Warning size={12} />{w}</p>)}
    </div>
  </label>
);

export const MeetingOwnerSync = ({ buildingId, meetingId, onSynced }) => {
  const [diffs, setDiffs] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const check = useCallback(async (openAfter = false) => {
    try {
      const { data } = await axios.get(`${API}/buildings/${buildingId}/meetings/${meetingId}/owner-sync`);
      setDiffs(data.differences);
      setSel(Object.fromEntries(data.differences.map((d) => [d.apartment_id, d.recommended])));
      if (openAfter) {
        if (data.differences.length) setOpen(true);
        else toast.success('Τα στοιχεία ιδιοκτητών είναι ήδη ενημερωμένα');
      }
    } catch (e) {
      if (openAfter) toast.error(e.response?.data?.detail || 'Αποτυχία ελέγχου');
    }
  }, [buildingId, meetingId]);

  useEffect(() => { check(false); }, [check]);

  const apply = async () => {
    const rows = diffs.filter((d) => sel[d.apartment_id]).map((d) => ({ apartment_id: d.apartment_id, fields: Object.keys(d.changes) }));
    if (!rows.length) { toast.error('Επιλέξτε τουλάχιστον μία γραμμή'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/buildings/${buildingId}/meetings/${meetingId}/owner-sync`, { rows });
      toast.success(data.applied.length === 1 ? 'Ενημερώθηκε 1 πεδίο' : `Ενημερώθηκαν ${data.applied.length} πεδία`);
      setOpen(false);
      await check(false);
      onSynced && onSynced(data.applied);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Αποτυχία συγχρονισμού');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {diffs.length > 0 && (
        <div className="flex items-center justify-between gap-3 border border-amber-300 bg-amber-50 px-4 py-3" data-testid="owner-sync-banner">
          <p className="text-sm text-amber-900 flex items-center gap-2"><Warning size={16} />Τα στοιχεία ιδιοκτητών/διαμερισμάτων έχουν αλλάξει για {diffs.length} διαμέρισμα(τα) από τη σύνταξη των πρακτικών.</p>
          <Button size="sm" className="rounded-none bg-amber-600 hover:bg-amber-700" onClick={() => setOpen(true)} data-testid="owner-sync-review-btn">Προβολή & Συγχρονισμός</Button>
        </div>
      )}
      <Button size="sm" variant="outline" className="rounded-none" onClick={() => check(true)} data-testid="owner-sync-refresh-btn">
        <ArrowsClockwise size={14} className="mr-1" />Ανανέωση Στοιχείων Ιδιοκτητών
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="owner-sync-dialog">
          <DialogHeader><DialogTitle>Συγχρονισμός Στοιχείων Ιδιοκτητών</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">Ενημερώνονται μόνο το όνομα ιδιοκτήτη και ο αριθμός διαμερίσματος. Συζητήσεις, αποφάσεις, ψηφοφορίες και παρουσίες δεν αλλάζουν. Κάθε αλλαγή καταγράφεται στο ιστορικό.</p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {diffs.map((d) => <SyncRow key={d.apartment_id} d={d} checked={!!sel[d.apartment_id]} onToggle={() => setSel((s) => ({ ...s, [d.apartment_id]: !s[d.apartment_id] }))} />)}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)} data-testid="owner-sync-cancel">Άκυρο</Button>
            <Button className="rounded-none bg-zinc-900" disabled={busy} onClick={apply} data-testid="owner-sync-apply">{busy ? 'Ενημέρωση…' : 'Επιβεβαίωση Συγχρονισμού'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
